import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { toast } from "sonner";
import { Gift, Loader2, Check, AlertTriangle, ChevronDown } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const imgSrc = (u) => (!u ? "" : u.startsWith("http") ? u : `${BACKEND}${u}`);
const nf = (n) => (n ?? 0).toLocaleString("it-IT", { maximumFractionDigits: 1 });

function ReserveDialog({ product, partners, open, onOpenChange, onReserved }) {
  const [pid, setPid] = useState("");
  const [busy, setBusy] = useState(false);
  const selected = partners.find((p) => p.id === pid);
  const insufficient = selected && (selected.tot_saldo ?? 0) < product.costo_punti;

  useEffect(() => { if (!open) { setPid(""); setBusy(false); } }, [open]);

  const submit = async () => {
    if (!pid) return;
    setBusy(true);
    try {
      const r = await api.post("/catalog/redeem", { product_id: product.id, partner_id: pid });
      toast.success(r.data?.message || "Prenotazione effettuata");
      onOpenChange(false);
      onReserved?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="reserve-dialog">
        <DialogHeader>
          <DialogTitle>Prenota «{product.name}»</DialogTitle>
          <DialogDescription>
            Costo: <span className="font-mono font-bold text-primary">{product.costo_punti} Punti Vesta</span>. Seleziona il partner per cui prenotare.
          </DialogDescription>
        </DialogHeader>
        <Command className="rounded-lg border border-border">
          <CommandInput placeholder="Cerca partner…" data-testid="reserve-partner-search" />
          <CommandList className="max-h-64">
            <CommandEmpty>Nessun partner trovato.</CommandEmpty>
            <CommandGroup>
              {partners.map((p) => {
                const ok = (p.tot_saldo ?? 0) >= product.costo_punti;
                return (
                  <CommandItem key={p.id} value={p.name} onSelect={() => setPid(p.id)} data-testid={`reserve-partner-${p.id}`} className="flex items-center justify-between gap-3">
                    <span className="flex items-center gap-2">
                      {pid === p.id && <Check className="h-4 w-4 text-primary" />}
                      <span className={pid === p.id ? "font-semibold" : ""}>{p.name}</span>
                    </span>
                    <span className={`shrink-0 font-mono text-xs font-semibold ${ok ? "text-emerald-400" : "text-red-400"}`}>{nf(p.tot_saldo)} PV</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
        {selected && (
          <div className={`rounded-lg border p-3 text-sm ${insufficient ? "border-red-500/40 bg-red-500/10 text-red-300" : "border-emerald-500/30 bg-emerald-500/10 text-emerald-200"}`} data-testid="reserve-summary">
            {insufficient ? (
              <span className="flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Saldo insufficiente: {selected.name} ha {nf(selected.tot_saldo)} PV, servono {product.costo_punti}.</span>
            ) : (
              <span>Saldo di <b>{selected.name}</b>: {nf(selected.tot_saldo)} PV → dopo la prenotazione: <b>{nf((selected.tot_saldo ?? 0) - product.costo_punti)} PV</b></span>
            )}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="reserve-cancel">Annulla</Button>
          <Button onClick={submit} disabled={!pid || insufficient || busy} data-testid="reserve-confirm">
            {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Gift className="mr-1 h-4 w-4" />} Conferma prenotazione
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ProductCard({ p, canReserve, partners, onReserved }) {
  const [open, setOpen] = useState(false);
  const [descOpen, setDescOpen] = useState(false);
  return (
    <Card className="flex flex-col overflow-hidden" data-testid={`vetrina-product-${p.id}`}>
      <div className="aspect-video w-full overflow-hidden bg-muted">
        {p.image_url ? <img src={imgSrc(p.image_url)} alt={p.name} className="h-full w-full object-cover" /> : <div className="flex h-full w-full items-center justify-center text-muted-foreground"><Gift className="h-8 w-8" /></div>}
      </div>
      <div className="flex flex-1 flex-col gap-2 p-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-heading text-base font-bold leading-tight">{p.name}</h3>
          <Badge variant="outline" className="shrink-0 text-[10px]">{p.type === "servizio" ? "Servizio" : "Prodotto"}</Badge>
        </div>
        {p.description && (
          <div>
            <button type="button" onClick={() => setDescOpen((v) => !v)} className="flex items-center gap-1 text-xs font-semibold text-primary hover:underline" data-testid={`desc-toggle-${p.id}`}>
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${descOpen ? "rotate-180" : ""}`} />
              {descOpen ? "Nascondi descrizione" : "Mostra descrizione"}
            </button>
            {descOpen && <p className="mt-1.5 text-sm text-muted-foreground" data-testid={`desc-text-${p.id}`}>{p.description}</p>}
          </div>
        )}
        {p.specs?.length > 0 && (
          <table className="mt-1 w-full text-xs">
            <tbody>
              {p.specs.map((s, i) => (
                <tr key={i} className="border-b border-border/40">
                  <td className="py-1 pr-2 text-muted-foreground">{s.label}</td>
                  <td className="py-1 text-right font-medium">{s.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div className="mt-auto flex items-center justify-between pt-3">
          <div className="font-mono text-lg font-black text-primary">{p.costo_punti} <span className="text-xs font-semibold text-muted-foreground">Punti Vesta</span></div>
          {canReserve && (
            <Button size="sm" onClick={() => setOpen(true)} data-testid={`redeem-${p.id}`}><Gift className="mr-1 h-4 w-4" /> Prenota</Button>
          )}
        </div>
      </div>
      {canReserve && <ReserveDialog product={p} partners={partners} open={open} onOpenChange={setOpen} onReserved={onReserved} />}
    </Card>
  );
}

export default function CatalogoVesta() {
  const { user } = useAuth();
  const canReserve = ["admin", "head_of_partner"].includes(user?.role);
  const [cats, setCats] = useState([]);
  const [prods, setProds] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadPartners = () => api.get("/vesta/yearly").then((r) => setPartners(r.data || [])).catch(() => {});

  useEffect(() => {
    Promise.all([api.get("/catalog/categories"), api.get("/catalog/products")])
      .then(([c, p]) => { setCats(c.data || []); setProds(p.data || []); })
      .catch(() => {})
      .finally(() => setLoading(false));
    if (canReserve) loadPartners();
  }, [canReserve]);

  const byCat = (cid) => prods.filter((p) => p.category_id === cid);
  const orphan = prods.filter((p) => !p.category_id || !cats.some((c) => c.id === p.category_id));

  const cardProps = { canReserve, partners, onReserved: loadPartners };

  return (
    <div className="space-y-8" data-testid="catalogo-vesta-page">
      <div>
        <h1 className="font-heading text-3xl font-black tracking-tight">Catalogo Vesta</h1>
        <p className="text-sm text-muted-foreground">
          {canReserve ? "Vantaggi, servizi ed esperienze prenotabili ai partner con i Punti Vesta." : "Vantaggi, servizi ed esperienze del programma Punti Vesta."}
        </p>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Caricamento…</div>
      ) : prods.length === 0 ? (
        <Card className="flex flex-col items-center justify-center gap-3 p-16 text-center" data-testid="catalogo-vesta-empty">
          <Gift className="h-10 w-10 text-primary" />
          <div className="font-heading text-2xl font-black">In arrivo</div>
          <p className="max-w-md text-sm text-muted-foreground">Il catalogo dei vantaggi sarà disponibile a breve.</p>
        </Card>
      ) : (
        <div className="space-y-10">
          {cats.filter((c) => byCat(c.id).length > 0).map((c) => (
            <section key={c.id} data-testid={`vetrina-category-${c.id}`}>
              <div className="mb-4 flex items-center gap-4">
                {c.image_url && <div className="h-14 w-14 overflow-hidden rounded-lg bg-muted"><img src={imgSrc(c.image_url)} alt="" className="h-full w-full object-cover" /></div>}
                <div>
                  <h2 className="font-heading text-lg font-black">{c.name}</h2>
                  {c.description && <p className="text-sm text-muted-foreground">{c.description}</p>}
                </div>
              </div>
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
                {byCat(c.id).map((p) => <ProductCard key={p.id} p={p} {...cardProps} />)}
              </div>
            </section>
          ))}
          {orphan.length > 0 && (
            <section data-testid="vetrina-category-altro">
              <h2 className="mb-4 font-heading text-lg font-black">Altro</h2>
              <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">{orphan.map((p) => <ProductCard key={p.id} p={p} {...cardProps} />)}</div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
